import React from 'react';
import { render } from 'react-dom';
import { Router, browserHistory, match } from 'react-router';
import { Provider } from 'react-redux';
import ExecutionEnvironment from 'fbjs/lib/ExecutionEnvironment';
import FontFaceObserver from 'fontfaceobserver';

import { isWebPushSupported, sendApplicationHeartbeatForWebPush } from 'utils/webpushHelper';

import routes from './routes';
import clientStore from './stores/clientStore';
import scrollHistory from 'utils/scrollHistory';
import { proxiedOmnitureTrackPageView, proxiedOmnitureTrackLink } from 'utils/miscUtils';
import './networkStatus';
import { setNetworkTypeInCookie } from 'utils/miscUtils';

import { loadComponents } from 'fk-cp-bundle-loader/client';
import { logToSentry } from 'utils/ErrorLogging';
import 'regenerator-runtime/runtime';

const DEV = process.env.NODE_ENV !== 'production';
const STAGE = process.env.FK_ENV === 'stage';

if (ExecutionEnvironment.canUseDOM) {
	'scrollRestoration' in history && (history.scrollRestoration = 'manual');

	function proxyOmnitureTracking() {
		if (window.omniture) {
			window.omniture.trackPageView = proxiedOmnitureTrackPageView(clientStore, window.omniture.trackPageView);
			window.omniture.trackLink = proxiedOmnitureTrackLink(clientStore, window.omniture.trackLink);
		}
	}
	/** Omniture handling based on user state data */
	// use proxied function instead of actual function when `tpv` and `tl` is already populate and omniture library takes time to load
	if (window.omniture && (window.omniture.tpv || window.omniture.tl)) {
		// `tpv` and `tl` basically store the data in case when library is yet to load and make request with same set of tracking params once library loads
		proxyOmnitureTracking();
	}

	/** overrides omniture tracking library methods again as once the library loads it will override our proxied function written above
	 * Now here it can be two cases :
	 * 	1. script is already loaded and for some reason onload event doesn't get called (e.g. if its cached etc.)
	 * 		- to decide this we check `tpv` and `tl` event if both of it is not present that means script is already loaded
	 * 			Note: `tpv` and 'tl' are custom object which we mention in `index.hbs` file
	 * 	2. script is not loaded
	 * 		- if first condition fails it means script is yet to be loaded
	 */
	if (window.omniture && !window.omniture.tpv && !window.omniture.tl && window.omniture.trackPageView) {
		// script is loaded already
		proxyOmnitureTracking();
	} else {
		const omniScript = document.getElementById('omni_script');
		omniScript && omniScript.addEventListener('load', proxyOmnitureTracking);
	}

	/**
	 * The networkType values 'slow-2g', '2g', '3g', or '4g' will be sent to backend in every api call.
	 * This information gets used in sending the optimised response.
	 */
	const connection = window.navigator && window.navigator.connection;
	setNetworkTypeInCookie();
	connection && connection.addEventListener('change', setNetworkTypeInCookie);
}

console.log('Flipkart ' + String.fromCharCode(0x2665) + "'s web"); // eslint-disable-line

const historyWithScrollDetail = scrollHistory(browserHistory);

// This code is for windows app : Makes the topBar color same as windows app to get the native app experience

if (ExecutionEnvironment.canUseDOM && window && window.Windows) {
	const windows = window.Windows;
	try {
		const titleBar = windows.UI.ViewManagement.ApplicationView.getForCurrentView().titleBar;
		const Colors = windows.UI.Colors;
		const ColorHelper = windows.UI.ColorHelper;
		const themeColor = ColorHelper.fromArgb(1, 40, 116, 240);
		const hoverColor = ColorHelper.fromArgb(1, 24, 106, 239);
		const pressedColor = ColorHelper.fromArgb(1, 16, 97, 230);
		const inactiveColor = ColorHelper.fromArgb(1, 56, 126, 241);
		titleBar.backgroundColor = themeColor;
		titleBar.foregroundColor = Colors.White;
		titleBar.inactiveBackgroundColor = inactiveColor;
		titleBar.inactiveForegroundColor = Colors.white;
		titleBar.buttonPressedForegroundColor = Colors.white;
		titleBar.buttonPressedBackgroundColor = pressedColor;
		titleBar.buttonHoverForegroundColor = Colors.white;
		titleBar.buttonHoverBackgroundColor = hoverColor;
		titleBar.buttonForegroundColor = Colors.white;
		titleBar.buttonBackgroundColor = themeColor;
		titleBar.buttonInactiveBackgroundColor = inactiveColor;
		titleBar.buttonInactiveForegroundColor = Colors.white;
	} catch (e) {
		// doing nothing
	}
}

const renderOnClient = (routes, clientStore, renderProps, historyWithScrollDetail) => {
	return render(
		<Provider store={clientStore}>
			<Router {...renderProps} history={historyWithScrollDetail}>
				{routes}
			</Router>
		</Provider>,
		document.getElementById('container')
	);
};

match({ history: historyWithScrollDetail, routes }, (error, redirectLocation, renderProps) => {
	// Note: hydrate client irrespective of loadComponents sucess or failure
	// Promise.finally is not avaialble in IE-11 hence hydrateClient is called in Promise.then and Promise.catch
	loadComponents()
		.then(() => {
			renderOnClient(routes, clientStore, renderProps, historyWithScrollDetail);
		})
		.catch(error => {
			if (DEV || STAGE) {
				console.log(error);
			}
			renderOnClient(routes, clientStore, renderProps, historyWithScrollDetail);
			logToSentry(error, { fingerprint: ['client js error'] });
		});
});

const fontB = new FontFaceObserver('Roboto', { weight: 400 });
const fontC = new FontFaceObserver('Roboto', { weight: 500 });

isWebPushSupported() && sendApplicationHeartbeatForWebPush();

Promise.all([fontB.load(), fontC.load()])
	.then(function () {
		document.documentElement.className += ' fonts-loaded';
	})
	.catch(_ => console.error('failed to download fonts')); // eslint-disable-line no-console
